import React, { useState } from "react";
import styled from "styled-components";
import {
  useGetBalanceQuery,
  useGetBalanceHistoryQuery,
  useCreateWorkingWithdrawalMutation,
  useCreateTransitWithdrawalMutation,
  useGetTraderWithdrawalsQuery,
  useGetTransitWithdrawalAvailabilityQuery,
  useGetWithdrawalFeeQuery,
} from "../store/trader-api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
// Withdrawn fee is now fetched dynamically from API via useGetWithdrawalFeeQuery
const DEFAULT_WITHDRAWAL_FEE = 5; // fallback value

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const BalanceCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const BalanceCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const BalanceAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const BalanceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
`;

const InfoValue = styled.span`
  font-weight: 500;
  color: var(--text-primary);
`;

const ActionButton = styled.button<{
  variant?: "primary" | "secondary";
  disabled?: boolean;
}>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  ${({ variant, disabled }) => {
    if (disabled) {
      return `
        background: var(--bg-secondary);
        color: var(--text-muted);
        border: 1px solid var(--border-color);
      `;
    }

    if (variant === "secondary") {
      return `
        background: var(--text-secondary);
        color: #ffffff;
        &:hover {
          background: var(--text-primary);
          transform: translateY(-1px);
        }
      `;
    }

    return `
      background: var(--accent-primary);
      color: #ffffff;
      &:hover {
        background: var(--accent-primary-hover);
        transform: translateY(-1px);
      }
    `;
  }}
`;

const TablesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const TableCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  overflow: hidden;
`;

// Удалены неиспользуемые компоненты TableHeader и TableTitle

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;

  @media (max-width: 768px) {
    min-width: unset;
    font-size: 0.8125rem;

    /* Hide ID, Date, and "Before" columns on mobile */
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(4),
    td:nth-child(4) {
      display: none;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    overflow-x: hidden;
    margin: 0;
    padding: 0;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-track {
      background: var(--bg-secondary);
    }
    &::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
      border-radius: 4px;
    }
  }
`;

const HideOnMobile = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeaderRow = styled.thead`
  background: var(--bg-primary);
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.75rem;
  }

  &.hide-mobile {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const TableRow = styled.tr`
  &:hover {
    background: var(--bg-primary);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-divider);
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.8125rem;
  }

  &.hide-mobile {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Badge = styled.span<{ variant?: "success" | "warning" | "error" }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;

  ${({ variant = "success" }) => {
    switch (variant) {
      case "success":
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
        `;
      case "warning":
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
        `;
      case "error":
        return `
          background: var(--status-error-bg);
          color: var(--accent-error);
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-secondary);
        `;
    }
  }}
`;

// Модальное окно вывода
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1.25rem;
    width: 95%;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const MaxButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid var(--accent-primary);
  border-radius: 0.5rem;
  background: transparent;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-primary);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WithdrawBalanceInfo = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const WarningText = styled.div`
  color: var(--accent-warning);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${({ status }) => {
    switch (status) {
      case "PENDING":
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
        `;
      case "APPROVED":
        return `
          background: var(--alert-info-bg);
          color: var(--alert-info-text);
        `;
      case "COMPLETED":
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
        `;
      case "REJECTED":
        return `
          background: var(--status-error-bg);
          color: var(--accent-error);
        `;
      case "CANCELLED":
        return `
          background: var(--bg-secondary);
          color: var(--text-muted);
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-muted);
        `;
    }
  }}
`;

const ErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessText = styled.div`
  color: var(--accent-success);
  font-weight: 500;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const WalletInfoCard = styled.div<{ isActive: boolean }>`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-top: 0.5rem;
`;

const WalletAddress = styled.div`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.8125rem;
  background: var(--bg-primary);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  margin: 0.5rem 0;
  word-break: break-all;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.15s;
  user-select: all;

  &:hover {
    border-color: var(--accent-primary);
    background: var(--accent-primary-light);
  }
`;

const TimeLeft = styled.div<{ isUrgent: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => (props.isUrgent ? "var(--accent-error)" : "var(--text-secondary)")};
`;

const Alert = styled.div<{ type: "info" | "warning" | "error" | "success" }>`
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  background: ${(props) => {
    switch (props.type) {
      case "info":
        return "var(--alert-info-bg)";
      case "warning":
        return "var(--alert-warning-bg)";
      case "error":
        return "var(--alert-error-bg)";
      case "success":
        return "var(--alert-success-bg)";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.type) {
        case "info":
          return "var(--alert-info-border)";
        case "warning":
          return "var(--alert-warning-border)";
        case "error":
          return "var(--alert-error-border)";
        case "success":
          return "var(--alert-success-border)";
      }
    }};
  color: ${(props) => {
    switch (props.type) {
      case "info":
        return "var(--alert-info-text)";
      case "warning":
        return "var(--alert-warning-text)";
      case "error":
        return "var(--alert-error-text)";
      case "success":
        return "var(--alert-success-text)";
    }
  }};
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: 0.75rem 0.75rem 0 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "var(--bg-card)" : "transparent")};
  color: ${(props) => (props.active ? "var(--text-primary)" : "var(--text-secondary)")};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  transition: all 0.2s;

  &:hover {
    color: var(--text-primary);
    background: ${(props) =>
      props.active ? "var(--bg-card)" : "var(--bg-hover)"};
  }

  &:first-child {
    border-radius: 0.75rem 0 0 0;
  }
`;

// Компонент для отображения пополнений и выводов
const DepositsWithdrawalsContent: React.FC = () => {
  // Получаем данные о заявках на вывод провайдера
  // TODO: Добавить API для получения заявок на вывод для трейдеров
  // const { data: withdrawalRequestsData, isLoading: withdrawalRequestsLoading } =
  //   useGetProviderWithdrawalsQuery();
  const withdrawalRequestsData = null;
  const withdrawalRequestsLoading = false;

  // Получаем данные истории баланса для пополнений
  const { data: historyData, isLoading: historyLoading } =
    useGetBalanceHistoryQuery({
      limit: 50,
      type: "DEPOSIT", // Фильтруем только пополнения
    });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatUSDT = (amount: number) => {
    return `${amount.toFixed(2)} USDT`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
      case "SUCCESS":
        return "var(--accent-neutral)";
      case "PENDING":
      case "PROCESSING":
        return "var(--accent-primary)";
      case "FAILED":
      case "REJECTED":
        return "var(--accent-error)";
      default:
        return "var(--text-secondary)";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "Завершен";
      case "SUCCESS":
        return "Успешно";
      case "PENDING":
        return "Ожидает";
      case "PROCESSING":
        return "Обработка";
      case "FAILED":
        return "Неудача";
      case "REJECTED":
        return "Отклонен";
      default:
        return status || "—";
    }
  };

  if (withdrawalRequestsLoading || historyLoading) {
    return (
      <tbody>
        <tr>
          <TableCell colSpan={6}>
            <EmptyState>Загрузка...</EmptyState>
          </TableCell>
        </tr>
      </tbody>
    );
  }

  // Подготавливаем данные о пополнениях из истории баланса
  const historyArray = Array.isArray(historyData)
    ? historyData
    : historyData?.data || [];
  const deposits =
    historyArray.filter(
      (transaction: any) =>
        transaction.type === "DEPOSIT" ||
        (transaction.description &&
          transaction.description.includes("пополнение"))
    ) || [];

  // Подготавливаем данные о заявках на вывод
  const withdrawalRequests = withdrawalRequestsData
    ? (withdrawalRequestsData as any).data || []
    : [];

  // Объединяем и сортируем по дате
  const combinedData = [
    ...deposits.map((deposit: any) => ({
      type: "Пополнение",
      id: deposit.id,
      date: deposit.createdAt,
      amount: deposit.amount,
      status: "SUCCESS",
      description: deposit.description || "Пополнение баланса",
      balanceType: "—",
    })),
    ...withdrawalRequests.map((withdrawal: any) => ({
      type: `Заявка на вывод (${withdrawal.balanceType === "WORKING" ? "Рабочий" : "Переходной"})`,
      id: withdrawal.id,
      date: withdrawal.createdAt,
      amount: withdrawal.amount,
      status: withdrawal.status || "PENDING",
      description: withdrawal.comment || "Заявка на вывод",
      balanceType:
        withdrawal.balanceType === "WORKING" ? "Рабочий" : "Переходной",
      walletAddress: withdrawal.walletAddress,
      adminComment: withdrawal.adminComment,
      txHash: withdrawal.txHash,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  if (combinedData.length === 0) {
    return (
      <tbody>
        <tr>
          <TableCell colSpan={6}>
            <EmptyState>Нет операций пополнения или вывода</EmptyState>
          </TableCell>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {combinedData.map((item: any) => (
        <TableRow key={`${item.type}-${item.id}`}>
          <TableCell>
            <span
              style={{
                color: item.type === "Пополнение" ? "var(--accent-neutral)" : "var(--accent-primary)",
                fontWeight: "500",
              }}
            >
              {item.type}
            </span>
          </TableCell>
          <TableCell>{item.id?.slice(-8) || "—"}</TableCell>
          <TableCell>{formatDate(item.date)}</TableCell>
          <TableCell>
            <span
              style={{
                color: item.type === "Пополнение" ? "var(--accent-neutral)" : "var(--accent-error)",
                fontWeight: "500",
              }}
            >
              {item.type === "Пополнение" ? "+" : "-"}
              {formatUSDT(Math.abs(item.amount))}
            </span>
          </TableCell>
          <TableCell>
            <span
              style={{
                color: getStatusColor(item.status),
                fontWeight: "500",
              }}
            >
              {getStatusLabel(item.status)}
            </span>
          </TableCell>
          <TableCell>{item.description}</TableCell>
        </TableRow>
      ))}
    </tbody>
  );
};

function Balance() {
  const { data: balanceData } = useGetBalanceQuery();
  const { data: historyData } = useGetBalanceHistoryQuery({ limit: 50 });
  
  // API для выводов трейдеров
  const [createWorkingWithdrawal, { isLoading: isCreatingWorking }] =
    useCreateWorkingWithdrawalMutation();
  const [createTransitWithdrawal, { isLoading: isCreatingTransit }] =
    useCreateTransitWithdrawalMutation();
  const { data: transitAvailability } =
    useGetTransitWithdrawalAvailabilityQuery();
  const { data: withdrawalHistory } = useGetTraderWithdrawalsQuery();
  const { data: withdrawalFeeData } = useGetWithdrawalFeeQuery();

  // Dynamic withdrawal fee from API with fallback
  const withdrawalFee = withdrawalFeeData?.fee ?? DEFAULT_WITHDRAWAL_FEE;

  // Алиас для обратной совместимости
  const createWithdrawal = createWorkingWithdrawal;
  const isCreating = isCreatingWorking;

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTransitWithdrawModal, setShowTransitWithdrawModal] =
    useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "working" | "frozen" | "transit" | "deposits-withdrawals"
  >("working");
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    walletAddress: "",
  });
  const [transitWithdrawForm, setTransitWithdrawForm] = useState({
    amount: "",
    walletAddress: "",
    comment: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Состояния для пополнения
  const [depositStep, setDepositStep] = useState(1);
  const [depositForm, setDepositForm] = useState({
    amount: "",
  });
  const [reservedWallet, setReservedWallet] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRequestingWallet, setIsRequestingWallet] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const formatUSDT = (amount: number) => {
    return `${amount.toFixed(2)} USDT`;
  };

  // Функция для форматирования описаний транзакций (сокращение до 2 знаков)
  const formatTransactionDescription = (description: string, type?: string) => {
    if (!description) return "—";

    // Специальная обработка для дохода от ордера
    if (type === "ORDER_INCOME" && description.includes("Доход за ордер")) {
      const orderIdMatch = description.match(/ордер ([A-Z_0-9]+)/);
      const orderId = orderIdMatch ? orderIdMatch[1] : "неизвестен";
      return `💰 Доход от завершения ордера ${orderId}`;
    }

    // Заменяем длинные числа на округленные до 2 знаков
    return description.replace(/(\d+\.\d{3,})/g, (match) => {
      const num = parseFloat(match);
      return num.toFixed(2);
    });
  };

  const getBalanceTypeLabel = (balanceType: string) => {
    switch (balanceType) {
      case "WORKING":
        return "Рабочий";
      case "FROZEN":
        return "Замороженный";
      case "TRANSIT":
        return "Переходной";
      default:
        return balanceType || "—";
    }
  };

  const getBalanceTypeColor = (balanceType: string) => {
    switch (balanceType) {
      case "WORKING":
        return "var(--accent-neutral)";
      case "FROZEN":
        return "var(--accent-primary)";
      case "TRANSIT":
        return "var(--accent-primary)";
      default:
        return "var(--text-secondary)";
    }
  };

  // Функция для определения, является ли транзакция доходом провайдера
  const isProviderCommissionTransaction = (transaction: any) => {
    return (
      transaction.type === "COMMISSION" ||
      transaction.description?.includes("Комиссия провайдера") ||
      transaction.description?.includes("комиссия провайдера")
    );
  };

  // Переклассифицируем комиссии провайдера как транзакции переходного баланса
  const reclassifyTransactions = (transactions: any[]) => {
    return transactions.map((transaction) => {
      if (isProviderCommissionTransaction(transaction)) {
        return {
          ...transaction,
          type: "TRANSFER_TO_TRANSIT",
          description: transaction.description
            ?.replace(/комиссии мерчанта \(\d+%\)/gi, "")
            .trim(),
        };
      }
      return transaction;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setSuccess("Адрес скопирован в буфер обмена");
        setTimeout(() => setSuccess(""), 2000);
      });
    } else {
      // Fallback for non-secure contexts
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setSuccess("Адрес скопирован в буфер обмена");
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        console.error('Unable to copy to clipboard', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Проверяем наличие зарезервированного кошелька при загрузке
  React.useEffect(() => {
    if (showDepositModal) {
      checkReservedWallet();
    }
  }, [showDepositModal]);

  // Таймер для обновления времени резервирования кошелька
  React.useEffect(() => {
    if (!reservedWallet) return;

    const timer = setInterval(async () => {
      const now = new Date().getTime();
      const reserved = new Date(reservedWallet.reservedUntil).getTime();
      const diff = Math.max(0, Math.floor((reserved - now) / 1000));

      setTimeLeft(diff);

      if (diff <= 0) {
        setReservedWallet(null);
        setDepositStep(1);
        clearInterval(timer);
      }

      // Проверяем статус депозита каждые 5 секунд
      if (diff > 0 && diff % 5 === 0) {
        try {
          const response = await fetch(`${API_URL}/wallets/my-reserved`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            if (!data || !data.address) {
              // Кошелёк больше не зарезервирован — считаем, что депозит успешен
              setReservedWallet(null);
              setDepositStep(3); // Переход на экран успеха
              clearInterval(timer);
            }
          }
        } catch (error) {
          console.error("Ошибка при проверке статуса кошелька:", error);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [reservedWallet]);

  const checkReservedWallet = async () => {
    try {
      const response = await fetch(
        `${API_URL}/wallets/my-reserved`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.address) {
          setReservedWallet(data);
          setTimeLeft(data.timeLeft);
          setDepositStep(2);
          if (data.reservedAmount) {
            setDepositForm((prev) => ({
              ...prev,
              amount: data.reservedAmount.toString(),
            }));
          }
        }
      }
    } catch (error) {
      console.error("Ошибка при проверке кошелька:", error);
    }
  };

  const requestWallet = async () => {
    setIsRequestingWallet(true);
    setError("");

    try {
      const response = await fetch(
        `${API_URL}/wallets/reserve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: parseFloat(depositForm.amount),
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReservedWallet(data);
        setTimeLeft(data.timeLeft || 900);
        setDepositStep(2);
      } else {
        const error = await response.json();
        setError(error.message || "Ошибка при запросе кошелька");
      }
    } catch (error) {
      console.error("Ошибка при запросе кошелька:", error);
      setError("Ошибка при запросе кошелька");
    } finally {
      setIsRequestingWallet(false);
    }
  };

  const releaseWallet = async () => {
    if (!reservedWallet) return;

    try {
      const url = reservedWallet.walletId
        ? `${API_URL}/wallets/release/${reservedWallet.walletId}`
        : `${API_URL}/wallets/cancel-deposit/${reservedWallet.depositRequestId}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        setReservedWallet(null);
        setTimeLeft(0);
        setDepositStep(1);
        setShowCancelConfirm(false);
        setDepositForm({
          amount: "",
        });
      }
    } catch (error) {
      console.error("Ошибка при освобождении кошелька:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWithdrawForm({ ...withdrawForm, [name]: value });
    setError("");
  };

  // ✅ Максимальная сумма с учетом комиссии
  const handleMaxAmount = () => {
    // rawAvailableForWithdraw - это полный баланс
    // Нам нужно (Баланс - Комиссия) округлить вниз до 10
    const maxAmountWithFee = Math.max(0, Math.floor(rawAvailableForWithdraw / 10) * 10 - withdrawalFee);
    
    setWithdrawForm({
      ...withdrawForm,
      amount: maxAmountWithFee.toString(),
    });
    setError("");
  };

  const handleTransitInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTransitWithdrawForm({ ...transitWithdrawForm, [name]: value });
    setError("");
  };

  const handleTransitMaxAmount = () => {
    const maxPercent = transitAvailability?.maxWithdrawalPercent || 0;
    const maxByPercent = Math.floor(((transitBalance || 0) * maxPercent / 100) / 10) * 10;
    const maxWithFee = Math.max(0, maxByPercent - withdrawalFee);
    setTransitWithdrawForm({
      ...transitWithdrawForm,
      amount: maxWithFee.toString(),
    });
    setError("");
  };

  const handleTransitWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amount = parseFloat(transitWithdrawForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setError("Введите корректную сумму");
      return;
    }

    if (amount % 10 !== 0) {
      setError("Сумма должна быть кратной 10 USDT");
      return;
    }

    if (!transitWithdrawForm.walletAddress) {
      setError("Введите адрес кошелька");
      return;
    }

    // Проверяем лимит по проценту
    const maxPercent = transitAvailability?.maxWithdrawalPercent || 0;
    const maxByPercent = (transitBalance || 0) * maxPercent / 100;
    if (amount > maxByPercent) {
      setError(`Максимальная сумма: ${maxByPercent.toFixed(2)} USDT (${maxPercent}% от переходного баланса)`);
      return;
    }

    try {
      await createTransitWithdrawal({
        amount,
        currency: "USDT",
        walletAddress: transitWithdrawForm.walletAddress,
        comment: transitWithdrawForm.comment,
      }).unwrap();

      setShowTransitWithdrawModal(false);
      setTransitWithdrawForm({ amount: "", walletAddress: "", comment: "" });
      setSuccess("Заявка на вывод переходного баланса создана успешно");
    } catch (err: any) {
      setError(err?.data?.message || "Ошибка при создании заявки на вывод");
    }
  };

  const handleDepositInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDepositForm({ ...depositForm, amount: value });
    setError("");
    setSuccess("");
  };

  const handleDepositSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (depositStep === 1) {
      if (!depositForm.amount) {
        setError("Пожалуйста, введите сумму");
        return;
      }

      const amount = parseFloat(depositForm.amount);
      if (isNaN(amount) || amount <= 0) {
        setError("Пожалуйста, введите корректную сумму");
        return;
      }

      if (amount < 1) {
        setError("Минимальная сумма пополнения: 1 USDT");
        return;
      }

      await requestWallet();
    }
  };

  const handleCloseDepositModal = () => {
    setShowDepositModal(false);
    if (!reservedWallet) {
      setDepositStep(1);
      setDepositForm({
        amount: "",
      });
    }
    setError("");
    setSuccess("");
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amount = parseFloat(withdrawForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setError("Введите корректную сумму");
      return;
    }

    if (amount % 10 !== 0) {
      setError("Сумма должна быть кратна 10");
      return;
    }

    if (!withdrawForm.walletAddress) {
      setError("Введите адрес кошелька");
      return;
    }

    try {
      await createWithdrawal({
        amount,
        currency: "USDT",
        walletAddress: withdrawForm.walletAddress,
      }).unwrap();

      setShowWithdrawModal(false);
      setWithdrawForm({ amount: "", walletAddress: "" });
      setSuccess("Заявка на вывод создана успешно");
    } catch (err: any) {
      setError(err?.data?.message || "Ошибка при создании заявки на вывод");
    }
  };

  // ✅ ИСПРАВЛЕНО: Используем реальные данные из базы
  const workingBalance = balanceData?.balance || 0; // Рабочий баланс из БД
  const frozenBalance = balanceData?.frozenBalance || 0; // Замороженный баланс из БД
  const transitBalance = balanceData?.transitBalance || 0; // Переходной баланс из БД
  const disputeBalance = balanceData?.disputeBalance || 0; // Баланс в диспутах

  // ✅ Доступный баланс = рабочий - замороженный
  const availableBalance = workingBalance - frozenBalance;
  // Доступная сумма для вывода (округленная вниз до кратной 10)
  const rawAvailableForWithdraw = Math.max(0, availableBalance);
  const availableForWithdraw = Math.floor(rawAvailableForWithdraw / 10) * 10;

  const rawTransactions = historyData?.data || [];
  const transactions = reclassifyTransactions(rawTransactions);

  return (
    <BalanceContainer>
      <BalanceCardsGrid>
        {/* Рабочий баланс */}
        <BalanceCard>
          <CardTitle>Рабочий баланс</CardTitle>
          <BalanceAmount>{formatUSDT(workingBalance)}</BalanceAmount>
          <BalanceInfo>
            <InfoRow>
              <InfoLabel>В апелляциях:</InfoLabel>
              <InfoValue>{formatUSDT(disputeBalance)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Заморожено:</InfoLabel>
              <InfoValue>{formatUSDT(frozenBalance)}</InfoValue>
            </InfoRow>
          </BalanceInfo>
          <ButtonGroup>
            <ActionButton onClick={() => setShowDepositModal(true)}>
              Пополнить
            </ActionButton>
            <ActionButton
              variant="secondary"
              onClick={() => setShowWithdrawModal(true)}
            >
              Вывести
            </ActionButton>
          </ButtonGroup>
        </BalanceCard>

        {/* Переходной баланс */}
        <BalanceCard>
          <CardTitle>Переходной баланс</CardTitle>
          <BalanceAmount>{formatUSDT(transitBalance)}</BalanceAmount>
          <BalanceInfo>
            <InfoRow>
              <InfoLabel>Доступно через:</InfoLabel>
              <InfoValue>
                {transitAvailability?.isAvailable
                  ? "Доступен сейчас"
                  : `${transitAvailability?.daysLeft || 30} дней`}
              </InfoValue>
            </InfoRow>
          </BalanceInfo>
          <ActionButton
            variant="secondary"
            disabled={!transitAvailability?.isAvailable || transitBalance <= 0}
            onClick={() => setShowTransitWithdrawModal(true)}
          >
            Вывести
          </ActionButton>
        </BalanceCard>
      </BalanceCardsGrid>

      <TablesSection>
        <TableCard>
          <TabsContainer>
            <Tab
              active={activeTab === "working"}
              onClick={() => setActiveTab("working")}
            >
              История рабочего баланса
            </Tab>
            <Tab
              active={activeTab === "frozen"}
              onClick={() => setActiveTab("frozen")}
            >
              История замороженного баланса
            </Tab>
            <Tab
              active={activeTab === "transit"}
              onClick={() => setActiveTab("transit")}
            >
              История переходного баланса
            </Tab>
            <Tab
              active={activeTab === "deposits-withdrawals"}
              onClick={() => setActiveTab("deposits-withdrawals")}
            >
              Пополнения/Выводы
            </Tab>
          </TabsContainer>

          <TableWrapper>
          <Table>
            {activeTab === "working" && (
              <>
                <TableHeaderRow>
                  <tr>
                    <TableHeaderCell>ID</TableHeaderCell>
                    <TableHeaderCell>Создан</TableHeaderCell>
                    <TableHeaderCell>Тип баланса</TableHeaderCell>
                    <TableHeaderCell>До</TableHeaderCell>
                    <TableHeaderCell>Сумма</TableHeaderCell>
                    <TableHeaderCell>После</TableHeaderCell>
                    <TableHeaderCell>Описание</TableHeaderCell>
                  </tr>
                </TableHeaderRow>
                <tbody>
                  {transactions.filter(
                    (t) =>
                      !isProviderCommissionTransaction(t) &&
                      (t.balanceType === "WORKING" ||
                        t.balanceType === null ||
                        !t.balanceType)
                  ).length === 0 ? (
                    <tr>
                      <TableCell colSpan={7}>
                        <EmptyState>Нет операций с рабочим балансом</EmptyState>
                      </TableCell>
                    </tr>
                  ) : (
                    transactions
                      .filter(
                        (t) =>
                          !isProviderCommissionTransaction(t) &&
                          (t.balanceType === "WORKING" ||
                            t.balanceType === null ||
                            !t.balanceType)
                      )
                      .map((transaction: any) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {transaction.id?.slice(-8) || "—"}
                          </TableCell>
                          <TableCell>
                            {formatDate(transaction.createdAt)}
                          </TableCell>
                          <TableCell>
                            <span
                              style={{
                                color: getBalanceTypeColor(
                                  transaction.balanceType
                                ),
                                fontWeight: "500",
                              }}
                            >
                              {getBalanceTypeLabel(transaction.balanceType)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {formatUSDT(transaction.balanceBefore || 0)}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Определяем знак на основе типа операции и типа баланса
                              const isPositive =
                                transaction.type === "DEPOSIT" ||
                                transaction.type === "CREDIT" ||
                                transaction.type === "COMMISSION" ||
                                transaction.type === "ORDER_INCOME" || // ✅ Доход от ордера
                                transaction.type === "UNFREEZE" ||
                                transaction.type === "FREEZE_ADD" || // ✅ Добавление в замороженный баланс
                                transaction.type === "TRANSFER_TO_TRANSIT" ||
                                (transaction.balanceType === "WORKING" &&
                                  (transaction.type === "DEPOSIT" ||
                                    transaction.type === "CREDIT")) ||
                                (transaction.balanceType === "FROZEN" &&
                                  transaction.type === "FREEZE_ADD") || // ✅ Увеличение замороженного баланса
                                (transaction.balanceType === "TRANSIT" &&
                                  transaction.type === "TRANSFER_TO_TRANSIT"); // ✅ Доход в переходной баланс

                              return isPositive
                                ? `+${formatUSDT(Math.abs(transaction.amount))}`
                                : `-${formatUSDT(Math.abs(transaction.amount))}`;
                            })()}
                          </TableCell>
                          <TableCell>
                            {formatUSDT(transaction.balanceAfter || 0)}
                          </TableCell>
                          <TableCell>
                            {formatTransactionDescription(
                              (transaction.description || "—")
                                .replace(/комиссии мерчанта \(\d+%\)/gi, "")
                                .replace(
                                  /из \d+\.\d+ USDT комиссии мерчанта \(\d+%\)/gi,
                                  ""
                                )
                                .trim(),
                              transaction.type // ✅ Передаем тип операции
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </tbody>
              </>
            )}

            {activeTab === "frozen" && (
              <>
                <TableHeaderRow>
                  <tr>
                    <TableHeaderCell>ID</TableHeaderCell>
                    <TableHeaderCell>Создан</TableHeaderCell>
                    <TableHeaderCell>Тип баланса</TableHeaderCell>
                    <TableHeaderCell>До</TableHeaderCell>
                    <TableHeaderCell>Сумма</TableHeaderCell>
                    <TableHeaderCell>После</TableHeaderCell>
                    <TableHeaderCell>Описание</TableHeaderCell>
                  </tr>
                </TableHeaderRow>
                <tbody>
                  {transactions.filter((t) => t.balanceType === "FROZEN")
                    .length === 0 ? (
                    <tr>
                      <TableCell colSpan={7}>
                        <EmptyState>
                          Нет операций с замороженным балансом
                        </EmptyState>
                      </TableCell>
                    </tr>
                  ) : (
                    transactions
                      .filter((t) => t.balanceType === "FROZEN")
                      .map((transaction: any) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {transaction.id?.slice(-8) || "—"}
                          </TableCell>
                          <TableCell>
                            {formatDate(transaction.createdAt)}
                          </TableCell>
                          <TableCell>
                            <span
                              style={{
                                color: getBalanceTypeColor(
                                  transaction.balanceType
                                ),
                                fontWeight: "500",
                              }}
                            >
                              {getBalanceTypeLabel(transaction.balanceType)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // ✅ ИСПРАВЛЕНО: Для операций с замороженным балансом показываем логически правильные значения
                              if (transaction.type === "FREEZE_ADD") {
                                // При заморозке замороженного баланса: было 0.00
                                return formatUSDT(0);
                              } else if (
                                transaction.type === "DEDUCT" ||
                                transaction.type === "UNFREEZE_REMOVE"
                              ) {
                                // При списании: было равно сумме операции
                                return formatUSDT(transaction.amount);
                              }
                              // Для других операций используем исходное значение
                              return formatUSDT(transaction.balanceBefore || 0);
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Определяем знак на основе типа операции для замороженного баланса
                              const isPositive =
                                transaction.type === "FREEZE_ADD" ||
                                (transaction.balanceType === "FROZEN" &&
                                  transaction.type === "FREEZE_ADD");

                              return isPositive
                                ? `+${formatUSDT(Math.abs(transaction.amount))}`
                                : `-${formatUSDT(Math.abs(transaction.amount))}`;
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // ✅ ИСПРАВЛЕНО: Для операций с замороженным балансом показываем логически правильные значения
                              if (transaction.type === "FREEZE_ADD") {
                                // При заморозке: стало равно сумме
                                return formatUSDT(transaction.amount);
                              } else if (
                                transaction.type === "DEDUCT" ||
                                transaction.type === "UNFREEZE_REMOVE"
                              ) {
                                // При списании: стало 0.00
                                return formatUSDT(0);
                              }
                              // Для других операций используем исходное значение
                              return formatUSDT(transaction.balanceAfter || 0);
                            })()}
                          </TableCell>
                          <TableCell>
                            {formatTransactionDescription(
                              (transaction.description || "—")
                                .replace(/комиссии мерчанта \(\d+%\)/gi, "")
                                .replace(
                                  /из \d+\.\d+ USDT комиссии мерчанта \(\d+%\)/gi,
                                  ""
                                )
                                .trim(),
                              transaction.type // ✅ Передаем тип операции
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </tbody>
              </>
            )}

            {activeTab === "transit" && (
              <>
                <TableHeaderRow>
                  <tr>
                    <TableHeaderCell>Дата</TableHeaderCell>
                    <TableHeaderCell>Тип баланса</TableHeaderCell>
                    <TableHeaderCell>Сумма</TableHeaderCell>
                    <TableHeaderCell>Баланс до</TableHeaderCell>
                    <TableHeaderCell>Баланс после</TableHeaderCell>
                    <TableHeaderCell>Описание</TableHeaderCell>
                  </tr>
                </TableHeaderRow>
                <tbody>
                  {transactions.filter(
                    (t: any) =>
                      t.balanceType === "TRANSIT" ||
                      t.description?.includes("переходной") ||
                      t.description?.includes("Переходной") ||
                      t.description?.includes("30 дней") ||
                      t.type === "TRANSFER_TO_TRANSIT" ||
                      t.type === "TRANSFER_FROM_TRANSIT" ||
                      isProviderCommissionTransaction(t)
                  ).length === 0 ? (
                    <tr>
                      <TableCell colSpan={6}>
                        <EmptyState>
                          <div
                            style={{
                              fontSize: "1.5rem",
                              marginBottom: "0.5rem",
                            }}
                          >
                            ⏳
                          </div>
                          <div>Нет операций с переходным балансом</div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                              marginTop: "0.5rem",
                            }}
                          >
                            Операции появятся через 30 дней после получения
                            средств
                          </div>
                        </EmptyState>
                      </TableCell>
                    </tr>
                  ) : (
                    transactions
                      .filter(
                        (t: any) =>
                          t.balanceType === "TRANSIT" ||
                          t.description?.includes("переходной") ||
                          t.description?.includes("Переходной") ||
                          t.description?.includes("30 дней") ||
                          t.type === "TRANSFER_TO_TRANSIT" ||
                          t.type === "TRANSFER_FROM_TRANSIT" ||
                          isProviderCommissionTransaction(t)
                      )
                      .map((transaction: any, index: number) => (
                        <TableRow key={transaction?.id || index}>
                          <TableCell>
                            {formatDate(transaction.createdAt)}
                          </TableCell>
                          <TableCell>
                            <span
                              style={{
                                color: getBalanceTypeColor(
                                  transaction.balanceType
                                ),
                                fontWeight: "500",
                              }}
                            >
                              {getBalanceTypeLabel(transaction.balanceType)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Определяем знак на основе типа операции и типа баланса
                              const isPositive =
                                transaction.type === "TRANSFER_TO_TRANSIT" ||
                                transaction.type === "DEPOSIT" ||
                                transaction.type === "CREDIT" ||
                                transaction.type === "COMMISSION" ||
                                (transaction.balanceType === "TRANSIT" &&
                                  transaction.amount > 0);

                              return isPositive
                                ? `+${formatUSDT(Math.abs(transaction.amount))}`
                                : `-${formatUSDT(Math.abs(transaction.amount))}`;
                            })()}
                          </TableCell>
                          <TableCell>
                            {formatUSDT(transaction.balanceBefore || 0)}
                          </TableCell>
                          <TableCell>
                            {formatUSDT(transaction.balanceAfter || 0)}
                          </TableCell>
                          <TableCell>
                            {formatTransactionDescription(
                              (transaction.description || "—")
                                .replace(/комиссии мерчанта \(\d+%\)/gi, "")
                                .replace(
                                  /из \d+\.\d+ USDT комиссии мерчанта \(\d+%\)/gi,
                                  ""
                                )
                                .trim(),
                              transaction.type // ✅ Передаем тип операции
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </tbody>
              </>
            )}

            {activeTab === "deposits-withdrawals" && (
              <>
                <TableHeaderRow>
                  <tr>
                    <TableHeaderCell>Тип</TableHeaderCell>
                    <TableHeaderCell>ID</TableHeaderCell>
                    <TableHeaderCell>Дата</TableHeaderCell>
                    <TableHeaderCell>Сумма</TableHeaderCell>
                    <TableHeaderCell>Статус</TableHeaderCell>
                    <TableHeaderCell>Описание</TableHeaderCell>
                  </tr>
                </TableHeaderRow>
                <DepositsWithdrawalsContent />
              </>
            )}
          </Table>
          </TableWrapper>
        </TableCard>
      </TablesSection>

      {/* Модальное окно вывода */}
      {showWithdrawModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Вывод средств</ModalTitle>
              <CloseButton onClick={() => setShowWithdrawModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleWithdrawSubmit} autoComplete="off">
              <WithdrawBalanceInfo>
                <div>
                  <strong>Рабочий баланс:</strong>{" "}
                  {formatUSDT(rawAvailableForWithdraw)}
                </div>
                <div>
                  <strong>Комиссия за вывод:</strong>{" "}
                  {formatUSDT(withdrawalFee)}
                </div>
                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '8px', marginTop: '8px' }}>
                  <strong>Максимум к выводу:</strong>{" "}
                  {formatUSDT(Math.max(0, Math.floor(rawAvailableForWithdraw / 10) * 10 - withdrawalFee))}
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '4px' }}>
                    (кратно 10 USDT)
                  </span>
                </div>
                {withdrawForm.amount && parseFloat(withdrawForm.amount) > 0 && (
                  <div style={{
                    background: 'var(--alert-info-bg)',
                    border: '1px solid var(--alert-info-border)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginTop: '12px'
                  }}>
                    <div style={{ color: 'var(--alert-info-text)', fontWeight: 600 }}>
                      Вы получите: {formatUSDT(parseFloat(withdrawForm.amount))}
                    </div>
                    <div style={{ color: 'var(--alert-info-text)' }}>
                      Будет списано с баланса: {formatUSDT(parseFloat(withdrawForm.amount) + withdrawalFee)}
                      <span style={{ fontSize: '0.875rem', marginLeft: '4px' }}>
                        ({withdrawForm.amount} + {withdrawalFee} комиссия)
                      </span>
                    </div>
                  </div>
                )}
              </WithdrawBalanceInfo>

              <FormGroup>
                <Label>Сумма вывода (USDT)</Label>
                <InputGroup>
                  <Input
                    type="number"
                    name="amount"
                    value={withdrawForm.amount}
                    onChange={handleInputChange}
                    placeholder="Введите сумму (кратную 10)"
                    step="10"
                    min="10"
                    max={Math.max(0, Math.floor(rawAvailableForWithdraw / 10) * 10 - withdrawalFee)}
                    style={{ flex: 1 }}
                  />
                  <MaxButton
                    type="button"
                    onClick={handleMaxAmount}
                    disabled={rawAvailableForWithdraw <= withdrawalFee}
                  >
                    Максимум
                  </MaxButton>
                </InputGroup>
              </FormGroup>

              <FormGroup>
                <Label>Адрес кошелька</Label>
                <Input
                  type="text"
                  name="walletAddress"
                  value={withdrawForm.walletAddress}
                  onChange={handleInputChange}
                  placeholder="Введите адрес USDT кошелька"
                />
              </FormGroup>

              {error && <ErrorText>{error}</ErrorText>}

              <ButtonGroup>
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Отмена
                </ActionButton>
                <ActionButton type="submit" disabled={isCreating}>
                  {isCreating ? "Создание..." : "Создать заявку"}
                </ActionButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Модальное окно пополнения */}
      {showDepositModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Пополнение баланса</ModalTitle>
              <CloseButton onClick={handleCloseDepositModal}>×</CloseButton>
            </ModalHeader>

            {depositStep === 1 ? (
              <form onSubmit={handleDepositSubmit} autoComplete="off">
                <FormGroup>
                  <Label htmlFor="amount">Сумма пополнения (USDT)</Label>
                  <Input
                    type="number"
                    id="amount"
                    name="amount"
                    step="0.01"
                    min="1"
                    value={depositForm.amount}
                    onChange={handleDepositInputChange}
                    placeholder="Введите сумму"
                  />
                  <div
                    data-testid="deposit-aml-hint"
                    style={{
                      marginTop: "0.375rem",
                      fontSize: "0.8125rem",
                      lineHeight: 1.4,
                      color: "var(--text-muted)",
                    }}
                  >
                    При уровне риска USDT выше 50% средства будут возвращены
                    отправителю на адрес отправки.
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Способ пополнения</Label>
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "var(--bg-primary)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "0.5rem",
                      color: "var(--text-secondary)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Криптовалюта (USDT TRC-20)
                  </div>
                </FormGroup>

                {error && <ErrorText>{error}</ErrorText>}
                {success && <SuccessText>{success}</SuccessText>}

                <ButtonGroup>
                  <ActionButton
                    type="button"
                    variant="secondary"
                    onClick={handleCloseDepositModal}
                  >
                    Отмена
                  </ActionButton>
                  <ActionButton type="submit" disabled={isRequestingWallet}>
                    {isRequestingWallet ? "Запрос кошелька..." : "Далее"}
                  </ActionButton>
                </ButtonGroup>
              </form>
            ) : depositStep === 2 ? (
              <div>
                {reservedWallet ? (
                  <WalletInfoCard isActive={true}>
                    <Alert type="info">
                      <strong>
                        Кошелёк зарезервирован на 30 минут
                      </strong>
                      <br />
                      Переведите {depositForm.amount} USDT (TRC-20) на адрес
                      ниже
                    </Alert>

                    <Label>Адрес для пополнения:</Label>
                    <WalletAddress
                      onClick={() => copyToClipboard(reservedWallet.address)}
                      title="Нажмите, чтобы скопировать"
                    >
                      {reservedWallet.address}
                    </WalletAddress>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "0.75rem",
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Сеть:</span>{" "}
                        <strong style={{ color: 'var(--text-primary)' }}>TRON (TRC-20)</strong>
                        <span style={{ margin: '0 0.5rem', color: 'var(--border-color)' }}>|</span>
                        <span style={{ color: 'var(--text-muted)' }}>Сумма:</span>{" "}
                        <strong style={{ color: 'var(--text-primary)' }}>{depositForm.amount} USDT</strong>
                      </div>
                      <TimeLeft isUrgent={timeLeft < 300}>
                        {formatTime(timeLeft)}
                      </TimeLeft>
                    </div>

                    <div style={{ marginTop: "1rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.8125rem" }}>
                      Ожидаем поступления средств. Окно обновится автоматически.
                    </div>

                    {error && <ErrorText>{error}</ErrorText>}
                    {success && <SuccessText>{success}</SuccessText>}

                    {showCancelConfirm ? (
                      <div style={{ marginTop: "1rem" }}>
                        <p style={{ marginBottom: "0.75rem", color: "var(--text-primary)", fontSize: "0.9375rem", textAlign: "center" }}>
                          Вы уверены, что хотите отменить бронирование?
                        </p>
                        <ButtonGroup>
                          <ActionButton
                            type="button"
                            onClick={releaseWallet}
                          >
                            Да
                          </ActionButton>
                          <ActionButton
                            type="button"
                            variant="secondary"
                            onClick={() => setShowCancelConfirm(false)}
                          >
                            Нет
                          </ActionButton>
                        </ButtonGroup>
                      </div>
                    ) : (
                      <ButtonGroup>
                        <ActionButton
                          type="button"
                          variant="secondary"
                          onClick={() => setShowCancelConfirm(true)}
                        >
                          Отменить
                        </ActionButton>
                        <ActionButton
                          type="button"
                          onClick={() => copyToClipboard(reservedWallet.address)}
                          disabled={!!success}
                        >
                          {success ? "Скопировано!" : "Скопировать адрес"}
                        </ActionButton>
                      </ButtonGroup>
                    )}
                  </WalletInfoCard>
                ) : (
                  <Alert type="error">
                    Не удалось зарезервировать кошелек. Попробуйте снова.
                  </Alert>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
                <div style={{ fontSize: "2rem", marginBottom: "1rem", color: "var(--accent-success)" }}>✓</div>
                <h3 style={{ fontSize: "1.25rem", color: "var(--accent-success)", marginBottom: "0.5rem" }}>
                  Средства успешно зачислены!
                </h3>
                <p style={{ color: "var(--text-secondary)", marginBottom: "2rem" }}>
                  Ваш баланс был пополнен на сумму около {depositForm.amount} USDT.
                </p>
                <ActionButton onClick={() => {
                   handleCloseDepositModal();
                   // Ждем секунду и обновляем страницу, чтобы баланс подтянулся точно, 
                   // хотя RTK Query может рефетчить сам, но лучше обновить для надёжности.
                   window.location.reload();
                }}>
                  Отлично
                </ActionButton>
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Модальное окно вывода переходного баланса */}
      {showTransitWithdrawModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Вывод переходного баланса</ModalTitle>
              <CloseButton onClick={() => setShowTransitWithdrawModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleTransitWithdrawSubmit} autoComplete="off">
              <WithdrawBalanceInfo>
                <div>
                  <strong>Переходной баланс:</strong>{" "}
                  {formatUSDT(transitBalance)}
                </div>
                <div>
                  <strong>Доступно к выводу:</strong>{" "}
                  {formatUSDT(Math.floor((transitBalance || 0) / 10) * 10)}
                </div>
                {transitBalance && transitBalance % 10 !== 0 && (
                  <WarningText>
                    Остаток {formatUSDT(transitBalance % 10)} останется на
                    балансе (сумма должна быть кратна 10 USDT)
                  </WarningText>
                )}
              </WithdrawBalanceInfo>

              <FormGroup>
                <Label>Сумма вывода (USDT)</Label>
                <InputGroup>
                  <Input
                    type="number"
                    name="amount"
                    value={transitWithdrawForm.amount}
                    onChange={handleTransitInputChange}
                    placeholder="Введите сумму (кратную 10)"
                    step="10"
                    min="10"
                    max={Math.floor((transitBalance || 0) / 10) * 10}
                    style={{ flex: 1 }}
                  />
                  <MaxButton
                    type="button"
                    onClick={handleTransitMaxAmount}
                    disabled={!transitBalance || transitBalance <= 0}
                  >
                    Максимум
                  </MaxButton>
                </InputGroup>
              </FormGroup>

              <FormGroup>
                <Label>Адрес кошелька</Label>
                <Input
                  type="text"
                  name="walletAddress"
                  value={transitWithdrawForm.walletAddress}
                  onChange={handleTransitInputChange}
                  placeholder="Введите адрес USDT кошелька"
                />
              </FormGroup>

              <FormGroup>
                <Label>Комментарий (необязательно)</Label>
                <Input
                  type="text"
                  name="comment"
                  value={transitWithdrawForm.comment}
                  onChange={handleTransitInputChange}
                  placeholder="Комментарий к заявке"
                />
              </FormGroup>

              {error && <ErrorText>{error}</ErrorText>}
              {success && <SuccessText>{success}</SuccessText>}

              <ButtonGroup>
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={() => setShowTransitWithdrawModal(false)}
                >
                  Отмена
                </ActionButton>
                <ActionButton type="submit" disabled={isCreatingTransit}>
                  {isCreatingTransit ? "Создание..." : "Создать заявку"}
                </ActionButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </BalanceContainer>
  );
}

export default Balance;
